import { createContext, type ReactNode, useContext, useState } from "react";

import { PopupMenu } from "./components/modals/PopupMenu";

export type ArrowTip = "top-left";

export type LayoutOptions = {
  arrowTip?: ArrowTip;
};

export type PopupMenuItem = { content: ReactNode; onClick: () => void };

export type PopupMenuFunction = (
  content: ReactNode,
  position: { x: number; y: number },
  layoutOptions?: LayoutOptions,
) => void;

type PopupMenuContextType = {
  openPopupMenu: PopupMenuFunction;
  closePopup: () => void;
};

const PopupMenuContext = createContext<PopupMenuContextType | null>(null);

export const usePopupMenu = () => useContext(PopupMenuContext);

export const PopupMenuProvider = ({ children }: { children: ReactNode }) => {
  const [layoutOptions, setLayoutOptions] = useState<LayoutOptions>({});
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupItems, setPopupItems] = useState<ReactNode>();
  const [popupAnchor, setPopupPosition] = useState<{ x: number; y: number }>();

  const openPopup: PopupMenuFunction = (content, position, opts = {}) => {
    setPopupOpen(true);
    setPopupItems(content);
    setPopupPosition(position);
    setLayoutOptions(opts);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setPopupItems(undefined);
    setPopupPosition(undefined);
    setLayoutOptions({});
  };

  return (
    <PopupMenuContext.Provider value={{ closePopup, openPopupMenu: openPopup }}>
      {children}

      {popupOpen && (
        <PopupMenu
          open={popupOpen}
          onClose={closePopup}
          anchor={popupAnchor}
          layoutOptions={layoutOptions}
        >
          {popupItems}
        </PopupMenu>
      )}
    </PopupMenuContext.Provider>
  );
};
